import { css } from '@emotion/react'
import { Block } from 'components/Block'
import { Button } from 'components/button/Button'
import {
  SCard,
  SCardWrapper,
  SCoverImage,
  SProtectTheMemory,
  SProtectTheMemoryContent,
} from './ProtectTheMemory.styled'
import { Icon } from 'components/icon'
import { Spacer } from 'components/spacer/Spacer'
import { Text, Title, Title_h2_default, TTitleVariant } from 'components/Typography'
import Router from 'next/router'
import React, { ReactNode } from 'react'
import { mutateAnalyticsData } from 'utils/analytics'
import { useAuth } from 'utils/auth/hooks/useAuth'

export interface IProtectTheMemory {
  title?: string
  className?: string
  compact?: boolean
  titleVariant?: TTitleVariant
  description?: string
  image?: string
  cards?: Omit<MemoryCardProps, 'onClick'>[]
}

type MemoryCardProps = {
  onClick: () => void
  icon: ReactNode
  title: string
  dataTestId: string
  text: string
}

function MemoryCard(props: MemoryCardProps) {
  return (
    <SCard onClick={props.onClick} data-testid={props.dataTestId}>
      {props.icon}
      <Spacer size={0.5} />
      <Text variant={'bodyLarge'} css={{ color: 'white', marginBottom: '0.75rem' }}>
        {props.title}
      </Text>
      <Text variant={'bodySmall'} css={{ color: 'var(--c-gray400)' }}>
        {props.text}
      </Text>
    </SCard>
  )
}

export const ProtectTheMemory: React.FC<React.PropsWithChildren<IProtectTheMemory>> = ({
  title = 'Protect the memory of your loved ones too.',
  className,
  compact = false,
  description = 'Take charge of social media sites quickly to protect the memory of your loved ones. Secure important photos and other priceless content and stop paying subscriptions quickly.',
  titleVariant,
  cards = [
    {
      icon: <Icon name="Plus" color="white" size="2rem" />,
      title: 'Protect your loved ones',
      dataTestId: 'protect_your_loved_ones',
      text: 'Select which sites and social media accounts are most valuable.',
    },
    {
      icon: <Icon name="ColorFacebook" color="white" size="2rem" />,
      title: 'Memorialize Facebook',
      dataTestId: 'memorialize_facebook',
      text: 'Memorialize Facebook pages to protect the legacy of loved ones.',
    },
    {
      icon: <Icon name="ColorGoogle" color="white" size="2rem" />,
      title: 'Extract Google Photos',
      dataTestId: 'extract_google_photos',
      text: 'Make sure that all your invaluable photos and videos are not lost.',
    },
    {
      icon: <Icon name="ColorNetflix" color="white" size="2rem" />,
      title: 'Stop Netflix subscription',
      dataTestId: 'stop_netflix_subscription',
      text: 'Annoyed by recurring payments of Netflix? Take action right now.',
    },
  ],
  image,
}) => {
  const { isLogged } = useAuth()
  const onCardClick = async () => {
    if (isLogged) {
      await Router.push('/me/order')
    } else {
      mutateAnalyticsData({ button: 'wgs_card' })
      await Router.push('/wgs')
    }
  }

  return (
    <Block className={className} css={{ overflow: 'hidden' }}>
      <SProtectTheMemory>
        <SProtectTheMemoryContent compact={compact}>
          <div>
            <Title variant={titleVariant || Title_h2_default} as="h2" css={{ color: 'white' }}>
              {title}
            </Title>

            <Spacer size={[2, 1.2]} />

            <Text
              css={css`
                color: var(--c-gray400);
                margin-bottom: 2.5rem;
                max-width: 31.5rem;
              `}
            >
              {description}
            </Text>

            {compact && (
              <Button
                type="button"
                variant="text"
                size="large"
                href={isLogged ? '/me/order' : '/wgs'}
                data-testid="protectTheMemory-get-started"
                leftIcon={<Icon name="Plus" />}
              >
                Get started
              </Button>
            )}
          </div>

          <SCardWrapper>
            {cards?.map((card) => (
              <MemoryCard
                key={card.title}
                onClick={onCardClick}
                icon={card.icon}
                title={card.title}
                dataTestId={card.dataTestId}
                text={card.text}
              />
            ))}
          </SCardWrapper>
        </SProtectTheMemoryContent>

        {!compact && (
          <SCoverImage
            css={css`
              background-image: url(${image});
            `}
          />
        )}
      </SProtectTheMemory>
    </Block>
  )
}
